import { useRef, useEffect, useCallback } from 'react';

/**
 * useDebouncedCallback
 * Returns a stable debounced version of a callback that will only run
 * after the specified delay has elapsed since the last invocation.
 *
 * - Always uses the latest callback reference (no stale closure issues)
 * - Cleans up the timeout on unmount
 *
 * @param callback Function to debounce
 * @param delay Delay in ms
 */
export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const callbackRef = useRef<T>(callback);
  const timeoutRef = useRef<number | undefined>();

  // Keep ref updated with latest callback
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

export default useDebouncedCallback;